import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_URL}/api`;

// Global interceptor: attach Bearer token to every axios call.
// NOTE: We DO NOT set withCredentials=true globally. Mixing cookies with
// wildcard CORS origins breaks browsers (Firefox/Chrome reject). Auth is
// JWT-in-Authorization-header; the httpOnly cookie is a future SOC 2 hook.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const axiosInstance = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor: still send Bearer header as fallback for backward compat
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor: handle 401
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    if (response.data.access_token) {
      // Keep localStorage as fallback, but httpOnly cookie is primary
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  register: async (userData) => {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data;
  },

  logout: async () => {
    try { await axiosInstance.post('/auth/logout'); } catch (e) { console.warn('[auth.logout] server logout failed:', e?.message || e); }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  getMe: async () => {
    const response = await axiosInstance.get('/auth/me');
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },
};

export const transactionService = {
  getMerchantTransactions: async (limit = 100, skip = 0) => {
    const response = await axiosInstance.get(`/merchant/transactions?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  createTransaction: async (transactionData) => {
    const response = await axiosInstance.post('/merchant/transactions', transactionData);
    return response.data;
  },
  getDashboard: async () => {
    const response = await axiosInstance.get('/merchant/dashboard');
    return response.data;
  },
};

export const apiKeyService = {
  getApiKeys: async () => {
    const response = await axiosInstance.get('/merchant/api-keys');
    return response.data;
  },
  createApiKey: async (name) => {
    const response = await axiosInstance.post('/merchant/api-keys', { name });
    return response.data;
  },
  revokeApiKey: async (keyId) => {
    const response = await axiosInstance.delete(`/merchant/api-keys/${keyId}`);
    return response.data;
  },
};

export const congregationService = {
  getSummary: async () => {
    const response = await axiosInstance.get('/merchant/congregations');
    return response.data;
  },
  create: async (name, code) => {
    const response = await axiosInstance.post('/merchant/congregations', { name, code });
    return response.data;
  },
  regenerateKey: async (projectId) => {
    const response = await axiosInstance.post(`/merchant/congregations/${projectId}/regenerate-key`);
    return response.data;
  },
  revokeKey: async (projectId) => {
    const response = await axiosInstance.post(`/merchant/congregations/${projectId}/revoke-key`);
    return response.data;
  },
  getBankAccount: async (projectId) => {
    const response = await axiosInstance.get(`/merchant/congregations/${projectId}/bank-account`);
    return response.data;
  },
  saveBankAccount: async (projectId, payload) => {
    const response = await axiosInstance.put(`/merchant/congregations/${projectId}/bank-account`, payload);
    return response.data;
  },
  requestWithdrawal: async (projectId, amount, concept) => {
    const response = await axiosInstance.post(`/merchant/congregations/${projectId}/withdrawals`, { amount, concept });
    return response.data;
  },
  listWithdrawals: async (projectId) => {
    const response = await axiosInstance.get('/merchant/withdrawals', { params: projectId ? { project_id: projectId } : {} });
    return response.data;
  },
  getActivity: async (projectId) => {
    const response = await axiosInstance.get(`/merchant/congregations/${projectId}/activity`);
    return response.data;
  },
};

export const adminService = {
  getAllUsers: async () => {
    const response = await axiosInstance.get('/admin/users');
    return response.data;
  },
  updateUserStatus: async (userId, status) => {
    const response = await axiosInstance.put(`/admin/users/${userId}/status`, null, { params: { new_status: status } });
    return response.data;
  },
  getStats: async () => {
    const response = await axiosInstance.get('/admin/stats');
    return response.data;
  },
};

export const bancoPlazaService = {
  getBanks: async () => {
    const response = await axiosInstance.get('/banco-plaza/banks');
    return response.data;
  },
};

export default axiosInstance;
